import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, ExternalLink, ArrowRight, CheckCircle, AlertCircle,
  Clock, BookOpen, GraduationCap, Building2, Target
} from 'lucide-react';

const ScoreSending = () => {
  const testingAgencies = [
    {
      name: "College Board (SAT & AP)",
      description: "Send SAT scores and AP exam scores to colleges through your College Board account.",
      link: "https://www.collegeboard.org/",
      sendLink: "https://studentscores.collegeboard.org/sendscores",
      logo: "📝",
      features: [
        "Free score sends with registration",
        "Send to up to 4 colleges when you register",
        "Additional sends cost $12 each",
        "Score Choice lets you choose which scores to send"
      ],
      steps: [
        "Create or log in to your College Board account",
        "Go to 'Send Scores' section",
        "Search for colleges by name or code",
        "Select scores to send and confirm payment"
      ]
    },
    {
      name: "ACT",
      description: "Send ACT scores directly to colleges and scholarship programs.",
      link: "https://www.act.org/",
      sendLink: "https://my.act.org/",
      logo: "📋",
      features: [
        "Free score sends with registration",
        "Send to up to 4 colleges when you register",
        "Additional sends cost $15 each",
        "Superscore option available at some colleges"
      ],
      steps: [
        "Log in to your ACT Web Account",
        "Navigate to 'Send Your Scores'",
        "Search for institutions",
        "Select test date(s) and complete order"
      ]
    },
    {
      name: "TOEFL (ETS)",
      description: "Send TOEFL iBT scores to demonstrate English proficiency.",
      link: "https://www.ets.org/toefl",
      sendLink: "https://www.ets.org/mytoefl",
      logo: "🌍",
      features: [
        "4 free score reports with registration",
        "Additional reports $20 each",
        "Scores valid for 2 years",
        "MyBest scores automatically included"
      ],
      steps: [
        "Log in to your ETS account",
        "Select 'Order Score Reports'",
        "Search by institution name or code",
        "Complete your order"
      ]
    },
    {
      name: "IELTS",
      description: "Request additional IELTS test reports to be sent to institutions.",
      link: "https://www.ielts.org/",
      sendLink: "https://ielts.org/take-a-test/test-results",
      logo: "🎯",
      features: [
        "5 free reports with test registration",
        "Additional reports vary by test center",
        "Results valid for 2 years",
        "Electronic delivery available"
      ],
      steps: [
        "Contact your IELTS test center",
        "Or request online through IELTS website",
        "Provide institution details",
        "Allow 3-5 business days for delivery"
      ]
    },
    {
      name: "Duolingo English Test",
      description: "Send unlimited free score reports to colleges worldwide.",
      link: "https://englishtest.duolingo.com/",
      sendLink: "https://englishtest.duolingo.com/applicants",
      logo: "🦉",
      features: [
        "Unlimited free score sends",
        "Results in 48 hours",
        "Scores valid for 2 years",
        "Accepted by 4,000+ institutions"
      ],
      steps: [
        "Log in to your Duolingo account",
        "Go to 'Send Results'",
        "Search for your institution",
        "Results sent instantly"
      ]
    }
  ];

  const tips = [
    {
      icon: <Clock className="h-5 w-5 text-blue-500" />,
      title: "Send Early",
      description: "Score reports can take 1-2 weeks to arrive. Send at least 3 weeks before deadlines."
    },
    {
      icon: <Target className="h-5 w-5 text-emerald-500" />,
      title: "Know Your Codes",
      description: "Each college has a unique code. Double-check codes before sending."
    },
    {
      icon: <CheckCircle className="h-5 w-5 text-purple-500" />,
      title: "Use Free Sends",
      description: "Take advantage of free score sends included with test registration."
    },
    {
      icon: <AlertCircle className="h-5 w-5 text-amber-500" />,
      title: "Check Requirements",
      description: "Some colleges are test-optional. Verify what's required before sending."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <FileText className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Score Sending Guide</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Send your official test scores directly to colleges
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-700 leading-relaxed">
              Most colleges require official test scores sent directly from testing agencies. 
              Self-reported scores may be accepted initially, but official reports are typically 
              required upon enrollment. Below are the main testing agencies and how to send scores.
            </p>
          </div>
          
          {/* Quick Tips */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {tips.map((tip, index) => (
              <Card key={index} className="text-center p-4">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-3">
                  {tip.icon}
                </div>
                <h4 className="font-semibold mb-1">{tip.title}</h4>
                <p className="text-sm text-gray-600">{tip.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testing Agencies */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Testing Agencies</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click on each agency to learn how to send your scores.
            </p>
          </div>

          <div className="space-y-8">
            {testingAgencies.map((agency, index) => (
              <Card key={index} className="overflow-hidden" data-testid={`agency-card-${index}`}>
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">{agency.logo}</span>
                      <div>
                        <CardTitle className="text-xl">{agency.name}</CardTitle>
                        <p className="text-gray-600 text-sm mt-1">{agency.description}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href={agency.link} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                          Website <ExternalLink className="ml-1 h-3 w-3" />
                        </Button>
                      </a>
                      <a href={agency.sendLink} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          Send Scores <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Features
                      </h4>
                      <ul className="space-y-2">
                        {agency.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="text-emerald-500 mt-1">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-blue-500" />
                        How to Send
                      </h4>
                      <ol className="space-y-2">
                        {agency.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full text-xs flex items-center justify-center font-medium">
                              {i + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Test Optional Notice */}
      <section className="py-12 bg-amber-50 border-t border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Test-Optional Policies</h3>
              <p className="text-amber-900/80 mb-4">
                Many colleges have adopted test-optional or test-blind policies. Before sending scores, 
                check each college's current testing requirements. You may be able to apply without 
                submitting test scores.
              </p>
              <Link to="/colleges">
                <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  Check College Requirements
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <GraduationCap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Track Your Applications</h2>
          <p className="text-gray-600 mb-8">
            Use your Wayo dashboard to track which colleges have received your scores and monitor application status.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-8">
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/colleges">
              <Button size="lg" variant="outline" className="px-8">
                Browse Colleges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScoreSending;
